import React, { useEffect } from 'react';
import { Alert, AlertActionLink, Bullseye, Form, FormGroup, Radio, Spinner, Title } from '@patternfly/react-core';
import type { GpuServerImage } from '../../../../types/models/GpuServer';

interface ImageStepProps {
  /** The product chosen on step 1 (`null` if none yet). */
  productId: number | null;
  /** The product's cached images; `null` until fetched (or after a failure). */
  images: GpuServerImage[] | null;
  loading: boolean;
  /** The images read's failure, shown verbatim with Retry. */
  loadError: string | null;
  /** Fetch the product's images unless cached (the cache lives above the wizard); `force` = Retry. */
  fetchImages: (productId: number, force?: boolean) => Promise<void>;
  selectedImage: string | null;
  onSelectImage: (code: string) => void;
  /** A start refusal that belongs to this step (`data.image`), shown verbatim. */
  error: string | null;
}

/**
 * Step 3 — choose the OS image. The list is a live read, fetched when this step is entered (PF mounts
 * a step body on entry) and cached per product above the wizard, so re-entering never refetches.
 */
const ImageStep: React.FunctionComponent<ImageStepProps> = ({
  productId,
  images,
  loading,
  loadError,
  fetchImages,
  selectedImage,
  onSelectImage,
  error,
}) => {
  useEffect(() => {
    if (productId !== null) {
      void fetchImages(productId);
    }
  }, [productId, fetchImages]);

  const renderBody = () => {
    if (productId === null) {
      return <Alert variant="info" isInline title="Choose a server first" />;
    }
    if (loadError !== null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="The system images could not be loaded"
          actionLinks={<AlertActionLink onClick={() => void fetchImages(productId, true)}>Retry</AlertActionLink>}
        >
          {loadError}
        </Alert>
      );
    }
    if (images === null || loading) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the system images" />
        </Bullseye>
      );
    }
    if (images.length === 0) {
      return (
        <Alert variant="warning" isInline title="No system image is available for this server right now.">
          Choose another server, or come back later.
        </Alert>
      );
    }
    return (
      // The wizard's own Next is a submit button, so this form must never submit natively.
      <Form onSubmit={(event) => event.preventDefault()}>
        <FormGroup role="radiogroup" fieldId="gpu-image" label="System image" isRequired isStack>
          {images.map((image) => (
            <Radio
              key={image.code}
              id={`gpu-image-${image.code}`}
              name="gpu-image"
              label={image.name ?? image.code}
              description={image.name !== null ? image.code : undefined}
              isChecked={selectedImage === image.code}
              onChange={() => onSelectImage(image.code)}
            />
          ))}
        </FormGroup>
      </Form>
    );
  };

  return (
    <>
      <Title headingLevel="h2" size="lg" className="pf-v6-u-mb-sm">
        Choose a system image
      </Title>
      {error !== null && (
        <Alert variant="danger" isInline title="This image cannot be used" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      {renderBody()}
    </>
  );
};

export default ImageStep;
